import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../Context/AuthProvider";
import { checkSecurityIdentifier, createSecurityMaster } from "../../http/api";
import { FIELD_TYPES, SECURITY_FIELDS, validateField } from "./updateSecurityConfig";

// Fields (by key) that are mandatory when creating a new security.
// identifier is enforced separately through the check step.
export const CREATE_REQUIRED_KEYS = new Set(["bondType"]);

const NON_IDENTIFIER_FIELDS = SECURITY_FIELDS.filter((f) => f.key !== "identifier");

const validateIdentifierFormat = (value) => {
  if (value.length < 9) return "Minimum length is 9 characters";
  if (value.length > 12) return "Maximum length is 12 characters";
  if (!/^[A-Z0-9]+$/.test(value)) return "Only uppercase letters (A-Z) and numbers (0-9) are allowed";
  return null;
};

export const useCreateSecurity = ({ open, onClose, onCreated }) => {
  const { token } = useAuth();

  const [identifierValue, setIdentifierValue] = useState("");
  const [identifierChecked, setIdentifierChecked] = useState(false);
  const [identifierCheckLoading, setIdentifierCheckLoading] = useState(false);
  const [identifierCheckError, setIdentifierCheckError] = useState(null);

  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Reset all state whenever the popup opens.
  useEffect(() => {
    if (!open) return;
    setIdentifierValue("");
    setIdentifierChecked(false);
    setIdentifierCheckLoading(false);
    setIdentifierCheckError(null);
    setFormValues({});
    setErrors({});
    setSubmitError(null);
    setLoading(false);
  }, [open]);

  // Auto-check identifier with 500ms debounce, matching the ActualOrderTab CUSIP pattern.
  useEffect(() => {
    if (!identifierValue.trim()) {
      setIdentifierChecked(false);
      setIdentifierCheckError(null);
      setIdentifierCheckLoading(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      const formatError = validateIdentifierFormat(identifierValue.trim());
      if (formatError) {
        setIdentifierCheckError(formatError);
        setIdentifierChecked(false);
        setIdentifierCheckLoading(false);
        return;
      }

      setIdentifierCheckLoading(true);
      setIdentifierCheckError(null);
      setIdentifierChecked(false);
      try {
        const result = await checkSecurityIdentifier(token, identifierValue.trim());
        if (!result) return; // forbidden redirect already handled
        if (result.identifierExists === false) {
          setIdentifierChecked(true);
          setFormValues({});
          setErrors({});
        } else {
          const msg = result.message || result.error || "Identifier already exists";
          setIdentifierCheckError(msg);
        }
      } catch (err) {
        setIdentifierCheckError(err.message || "Error checking identifier");
      } finally {
        setIdentifierCheckLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [identifierValue, token]);

  const handleIdentifierChange = useCallback((value) => {
    setIdentifierValue(value.toUpperCase());
    setIdentifierChecked(false);
    setIdentifierCheckError(null);
    setErrors((prev) => (prev.identifier ? { ...prev, identifier: "" } : prev));
  }, []);

  const handleChange = useCallback((key, value) => {
    const field = NON_IDENTIFIER_FIELDS.find((f) => f.key === key);
    if (field && field.type === FIELD_TYPES.NUMBER) {
      if (typeof value === "string" && value.startsWith("-")) return;
      if (field.max !== undefined) {
        const num = parseFloat(value);
        if (Number.isFinite(num) && num > field.max) return;
      }
      if (field.maxDecimals !== undefined && typeof value === "string") {
        const dotIdx = value.indexOf(".");
        if (dotIdx !== -1 && value.length - dotIdx - 1 > field.maxDecimals) return;
      }
    }
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
    setSubmitError(null);
  }, []);

  const validateAll = useCallback(() => {
    const nextErrors = {};
    if (!identifierValue.trim()) {
      nextErrors.identifier = "Identifier is required";
    } else if (identifierCheckLoading) {
      nextErrors.identifier = "Please wait while the identifier is being verified";
    } else if (!identifierChecked) {
      nextErrors.identifier = identifierCheckError || "Identifier could not be verified";
    }
    NON_IDENTIFIER_FIELDS.forEach((field) => {
      const value = formValues[field.key];
      if (CREATE_REQUIRED_KEYS.has(field.key)) {
        if (!value || String(value).trim() === "") {
          nextErrors[field.key] = `${field.label} is required`;
          return;
        }
      }
      const message = validateField(field, value);
      if (message) nextErrors[field.key] = message;
    });
    setErrors(nextErrors);
    return nextErrors;
  }, [identifierValue, identifierChecked, identifierCheckLoading, identifierCheckError, formValues]);

  const handleSubmit = useCallback(async () => {
    const validationErrors = validateAll();
    if (Object.keys(validationErrors).length > 0) return false;

    const payload = { identifier: identifierValue.trim() };
    NON_IDENTIFIER_FIELDS.forEach((field) => {
      const value = formValues[field.key];
      if (value === null || value === undefined || String(value).trim() === "") return;
      const trimmed = String(value).trim();
      payload[field.key] = field.type === FIELD_TYPES.NUMBER ? Number(trimmed) : trimmed;
    });

    setLoading(true);
    setSubmitError(null);
    try {
      const response = await createSecurityMaster(token, payload);
      if (onCreated) onCreated(response);
      if (onClose) onClose();
      return true;
    } catch (error) {
      setSubmitError(error.message || "Failed to create security. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [validateAll, identifierValue, formValues, token, onCreated, onClose]);

  return {
    identifierValue,
    identifierChecked,
    identifierCheckLoading,
    identifierCheckError,
    formValues,
    errors,
    submitError,
    loading,
    handleIdentifierChange,
    handleChange,
    handleSubmit,
  };
};
